import { Injectable, Injector } from '@angular/core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ISubProgramSecurityRequirementsAmountApiResponse,
  ISelectedBondResponse,
  IBondApiRequest,
  IBondSubmitResponse,
  IBondListResponse,
} from '../models/financial-security-api.model';
import {
  IBondDetails,
  ISelectedBondDetail,
  ISecurityRequirementInfo,
  BondTypes,
} from '../models/financial-security.model';
import { HttpParams } from '@angular/common/http';
import { IBaseResponse } from '@carm/_utils/models';
import { PaymentContext } from '@carm/_core/payments/models';
import { LoaderSpinnerTypes } from '@carm/_core';

@Injectable()
export class FinancialSecurityEnrolmentService extends BaseApiService {
  constructor(protected injector: Injector) {
    super(injector);
  }

  get serviceBaseUrlBP(): string {
    return `${this.endPointBP}/financial/security`;
  }

  get serviceBaseUrlB(): string {
    return `${this.endPointB}/financial/security`;
  }

  get serviceBaseUrlNB(): string {
    return `${this.getBaseUrl}/financial/security`;
  }

  private get getBaseUrl(): string {
    return environment.baseApiUrls;
  }

  public submitRppSelfAssessment(
    draftId: string,
    proposedSecurityAmount: number
  ): Observable<IBaseResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/enrolment/${draftId}/rpp/self-assessment`;
    return this.httpAuthPost<IBaseResponse>(endPoint, {
      proposedSecurityAmount,
    });
  }

  public applyPiecesOfEquipment(
    draftId: string,
    piecesOfEquipmentOperated: number
  ): Observable<ISubProgramSecurityRequirementsAmountApiResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/enrolment/${draftId}/hw-carrier/simulate`;
    return this.httpAuthPost<ISubProgramSecurityRequirementsAmountApiResponse>(
      endPoint,
      { nbEquipment: piecesOfEquipmentOperated }
    );
  }

  public startHwyCarrierEnrolment(
    draftId: string,
    piecesOfEquipmentOperated: number
  ): Observable<IBaseResponse> {
    const endPoint = `${this.serviceBaseUrlBP}/enrolment/${draftId}/hw-carrier/start-enrolment`;
    return this.httpAuthPost<IBaseResponse>(endPoint, {
      nbEquipment: piecesOfEquipmentOperated,
    });
  }

  public submitBondForDraft(
    bond: IBondDetails,
    context: PaymentContext
  ): Observable<any> {
    const endPoint = `${this.getFinancialSecurityEnrolmentBaseUrl(bond.draftId, context)}/bond`;
    return this.httpAuthPost<any>(endPoint, bond);
  }

  public getSubmittedBondForDraft(
    draftId: string,
    bondtype: string,
    number: string,
    context: PaymentContext
  ): Observable<ISelectedBondResponse> {
    const endPoint = `${this.getFinancialSecurityEnrolmentBaseUrl(draftId, context)}/bond/${number}/type/${bondtype}`;
    return this.httpAuthGet<ISelectedBondResponse>(endPoint);
  }

  public updateBondForDraft(change: IBondApiRequest, draftId: string, context: PaymentContext): Observable<IBaseResponse> {
    const endPoint = `${this.getFinancialSecurityEnrolmentBaseUrl(draftId, context)}/bond/update`;
    return this.httpAuthPut<IBaseResponse>(endPoint, change);
  }

  public submitActionBond(
    draftId: string,
    action: string,
    bond: ISelectedBondDetail,
    context: PaymentContext
  ): Observable<IBondSubmitResponse> {
    const endPoint = `${this.getFinancialSecurityEnrolmentBaseUrl(draftId, context)}/bond/${action}`;
    const req = {
      bn15: bond.bn15,
      id: bond.id,
      type: bond.type,
      suretyProvider: bond.suretyProvider,
    };
    if (bond.type !== 'Cash') {
      req.suretyProvider = bond.suretyProvider;
    }
    return this.httpAuthPut<any>(endPoint, req);
  }

  public getEnrolmentBonds(
    draftId: string,
    context: PaymentContext,
    pageIndex: number,
    pageSize: number,
    sortField: string,
    bondType: BondTypes,
  ): Observable<IBondListResponse> {
    const params = new HttpParams()
    .set('currentPage', pageIndex)
    .set('pageSize', pageSize)
    .set('bondsSort', sortField);
    const bondsType = bondType === BondTypes.Cash ? 'cash-bond-list' : 'e-bond-list';
    const baseUrl = this.getFinancialSecurityEnrolmentBaseUrl(draftId, context);
    return this.httpAuthGet<IBondListResponse>(`${baseUrl}/${bondsType}`, params, LoaderSpinnerTypes.AgGrid);
  }

  public getSecurityData(
    draftId: string,
    context: PaymentContext,
    loaderSpinnerType: LoaderSpinnerTypes
  ): Observable<ISecurityRequirementInfo> {
    const baseUrl = this.getFinancialSecurityEnrolmentBaseUrl(draftId, context);
    return this.httpAuthGet<ISecurityRequirementInfo>(
      `${baseUrl}/securitydata`, null, loaderSpinnerType
    );
  }

  public submitSecurityDeposit(
    draftId: string,
    context: PaymentContext
  ): Observable<IBaseResponse> {
    const baseUrl = this.getFinancialSecurityEnrolmentBaseUrl(draftId, context);
    return this.httpAuthPut<IBaseResponse>(`${baseUrl}/submit-security`, null);
  }

  private getFinancialSecurityEnrolmentBaseUrl(
    draftId: string,
    context: PaymentContext
  ): string {
    let endPoint: string;
    switch (context) {
      case PaymentContext.Business:
        endPoint = `${this.serviceBaseUrlB}/enrolment/${draftId}`;
        break;
      case PaymentContext.Program:
        endPoint = `${this.serviceBaseUrlBP}/enrolment/${draftId}`;
        break;
      case PaymentContext.Orphan:
        endPoint = `${this.serviceBaseUrlNB}/enrolment/${draftId}`;
        break;
    }
    return endPoint;
  }
}
